import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Icon } from './Icon';
import { api, ApiError, files } from '../lib/api';
import '../styles/templates.css';
import '../styles/crm.css';

const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW);
const linkFor = (url: string): string => `${window.location.origin}${PREVIEW ? '/#' : ''}${url}`;

export function ShareProfileModal({ cpId, cpName, onClose }: { cpId: string; cpName: string; onClose: () => void }) {
  const [url, setUrl] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.post<{ token: string; url: string }>(`/counterparties/${cpId}/share`, {})
      .then((r) => { setToken(r.token); setUrl(r.url); })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'No se pudo generar el enlace.'));
  }, [cpId]);

  const link = url ? linkFor(url) : '';

  useEffect(() => {
    if (!link) return;
    QRCode.toDataURL(link, { margin: 1, width: 352, color: { dark: '#000000ff', light: '#ffffffff' } })
      .then(setQr)
      .catch(() => setQr(null));
  }, [link]);

  const message = `Ficha de ${cpName}: ${link}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent(`Ficha de ${cpName}`)}&body=${encodeURIComponent(message)}`;

  const doCopy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* iframe */ }
  };
  const [pdfBusy, setPdfBusy] = useState(false);
  const doPdf = async () => {
    if (PREVIEW) { window.open(link, '_blank'); return; }
    setPdfBusy(true);
    try { await files.download(`/counterparties/${cpId}/pdf`, `${cpName}.pdf`); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo descargar la ficha.'); }
    finally { setPdfBusy(false); }
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 470 }}>
        <div className="modal__head">
          <div>
            <span className="eyebrow">Compartir ficha</span>
            <h2 className="h2" style={{ marginTop: 4, fontSize: 19 }}>{cpName}</h2>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>

        <div className="modal__body">
          {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
          {!err && !token && <div className="faint" style={{ padding: 20, textAlign: 'center' }}>Generando enlace…</div>}

          {token && (
            <>
              <div className="qr-box">
                {qr ? <img src={qr} alt="Profile QR code" /> : <div className="faint" style={{ padding: 40 }}>Generando QR…</div>}
              </div>
              <p className="qr-cap">Scan the code to open the full profile on mobile.</p>

              <div className="link-box" style={{ margin: '16px 0' }}>
                <div className="link-row">
                  <span className="link-code">{link}</span>
                  <button className="icon-btn" title="Copiar" onClick={doCopy}><Icon name={copied ? 'check' : 'copy'} size={16} /></button>
                </div>
              </div>

              <div className="share-actions">
                <a className="share-btn share-btn--wa" href={waUrl} target="_blank" rel="noreferrer">
                  <span className="share-btn__ico"><Icon name="whatsapp" size={18} /></span>
                  <span>WhatsApp<small>Enviar por chat</small></span>
                </a>
                <a className="share-btn share-btn--mail" href={mailUrl}>
                  <span className="share-btn__ico"><Icon name="mail" size={17} /></span>
                  <span>Email<small>Abrir en tu cliente</small></span>
                </a>
                <button className="share-btn share-btn--copy" onClick={doCopy}>
                  <span className="share-btn__ico"><Icon name={copied ? 'check' : 'copy'} size={16} /></span>
                  <span>{copied ? 'Copiado' : 'Copiar enlace'}<small>Al portapapeles</small></span>
                </button>
                <button className="share-btn share-btn--pdf" onClick={doPdf} disabled={pdfBusy}>
                  <span className="share-btn__ico"><Icon name="download" size={16} /></span>
                  <span>{pdfBusy ? 'Generando…' : 'Ficha en PDF'}<small>{PREVIEW ? 'Abrir e imprimir' : 'Blanco y negro'}</small></span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
